// ─── Feuille : choisir le contenu d'une CASE de la grille ──────────
// Une case porte soit un Repas (composition), soit une Recette seule, soit un
// texte libre (« Restaurant », « Restes »…). `repasList`/`recettes` déjà
// chargés par l'écran.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, ChoixPuces, Stepper, stylesForm, useUI, useT } from '@mop/core';
import { personnesRepas, personnesRecette } from './services/personnes';

const ONGLETS = [
  { id: 'repas', icone: '🍲', label: 'Repas' },
  { id: 'recette', icone: '🍽️', label: 'Recette' },
  { id: 'libre', icone: '✎', label: 'Libre' },
];

export default function FeuilleCase({ visible, cellule, repasList = [], recettes = [], onEnregistrer, onVider, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [onglet, setOnglet] = useState('repas');
  const [selection, setSelection] = useState(null);
  const [personnes, setPersonnes] = useState(4);
  const [libre, setLibre] = useState('');

  useEffect(() => {
    if (!visible) return;
    const c = cellule || {};
    if (c.repasId) {
      setOnglet('repas'); setSelection(c.repasId);
      setPersonnes(c.personnes || personnesRepas(repasList.find(r => r.id === c.repasId)));
    } else if (c.recetteId) {
      setOnglet('recette'); setSelection(c.recetteId);
      setPersonnes(c.personnes || personnesRecette(recettes.find(r => r.id === c.recetteId)));
    } else {
      setOnglet('repas'); setSelection(null); setPersonnes(4); setLibre(c.libre || '');
    }
  }, [visible, cellule]);

  const enregistrer = () => {
    if (onglet === 'libre') return onEnregistrer({ libre: (libre || '').trim() });
    if (onglet === 'repas' && selection) return onEnregistrer({ repasId: selection, personnes });
    if (onglet === 'recette' && selection) return onEnregistrer({ recetteId: selection, personnes });
  };
  const valide = onglet === 'libre' ? !!(libre || '').trim() : !!selection;

  return (
    <Feuille visible={visible} titre={t('repasPlan.ajouter')} emoji="🍽️" hauteur={0.75} onFermer={onFermer}
      action={{ icone: cellule ? '💾' : '＋', label: t('commun.enregistrer'), disabled: !valide, onPress: enregistrer }}>

      <ChoixPuces pleine options={ONGLETS.map(o => ({ id: o.id, label: `${o.icone} ${o.label}` }))}
        value={onglet} onChange={setOnglet} />

      {onglet !== 'libre' && (
        <>
          <Text style={[s.lbl, { marginTop: 14 }]}>
            {onglet === 'repas' ? t('repas.titre') : t('repasPlan.choisirRecette')}
          </Text>
          {(onglet === 'repas' ? repasList : recettes).length === 0 ? (
            <Text style={s.vide}>{t('repasPlan.aucuneRecette')}</Text>
          ) : (
            <View style={s.liste}>
              {(onglet === 'repas' ? repasList : recettes).map(item => (
                <TouchableOpacity key={item.id} style={[s.item, selection === item.id && s.itemSel]} activeOpacity={0.7}
                  onPress={() => { setSelection(item.id); setPersonnes(onglet === 'repas' ? personnesRepas(item) : personnesRecette(item)); }}>
                  <Text style={[s.itemTxt, selection === item.id && s.itemTxtSel]} numberOfLines={1}>
                    {onglet === 'repas' ? (item.icone || '🍲') : '🍽️'} {item.nom || t('repas.sansNom')}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
          {!!selection && (
            <>
              <Text style={s.lbl}>{t('repasPlan.nbPersonnes')}</Text>
              <Stepper valeur={personnes} onMoins={() => setPersonnes(Math.max(1, personnes - 1))} onPlus={() => setPersonnes(personnes + 1)} />
            </>
          )}
        </>
      )}

      {onglet === 'libre' && (
        <>
          <Text style={[s.lbl, { marginTop: 14 }]}>{t('repasPlan.ouLibre')}</Text>
          <TextInput style={s.input} value={libre} onChangeText={setLibre}
            placeholder={t('repasPlan.phLibre')} placeholderTextColor={UI.faint} />
        </>
      )}

      {!!cellule && (
        <TouchableOpacity style={s.vider} onPress={onVider}>
          <Text style={s.viderTxt}>🗑 {t('repasPlan.viderCase')}</Text>
        </TouchableOpacity>
      )}
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  vide: { fontSize: 13, color: UI.faint, lineHeight: 18, paddingVertical: 8 },
  liste: { gap: 6, marginBottom: 6 },
  item: { paddingVertical: 10, paddingHorizontal: 12, borderRadius: 10, backgroundColor: UI.card, borderWidth: 0.5, borderColor: UI.border },
  itemSel: { borderWidth: 2, borderColor: UI.accent, backgroundColor: UI.accentTint },
  itemTxt: { fontSize: 14, color: UI.ink, fontWeight: '600' },
  itemTxtSel: { color: UI.accent, fontWeight: '800' },
  vider: { marginTop: 16, alignItems: 'center', paddingVertical: 10 },
  viderTxt: { fontSize: 13.5, fontWeight: '700', color: UI.danger || '#c0392b' },
});
